import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import axios from 'axios';

import { IAlert, defaultValue } from 'app/shared/model/alert.model';
import { AlertCluster } from 'app/shared/model/enumerations/alert-cluster.model';
import { StatusFilter } from 'app/shared/model/enumerations/status-filter.model';
import { EntityState, IQueryParams, createEntitySlice, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { cleanEntity } from 'app/shared/util/entity-utils';
interface IPagedAlertParams extends IQueryParams {
  searchName?: string;
  searchCompanyName?: string;
  searchCluster?: AlertCluster | string;
  companyId?: number | string;
  status?: StatusFilter | string;
}

const initialState: EntityState<IAlert> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/alerts';

// Actions

export const getAlertDataSummmary = createAsyncThunk(
  'alert/fetch_entity_list',
  async ({ page, size, sort, searchName, searchCompanyName, searchCluster, status }: IPagedAlertParams) => {
    const company = `&searchCompanyName=${encodeURIComponent(searchCompanyName)}`;
    const alertName = `&searchName=${encodeURIComponent(searchName)}`;
    const alertCluster = `&searchCluster=${encodeURIComponent(searchCluster)}`;
    const alertStatus = `&status=${encodeURIComponent(status)}`;

    const requestUrl = `${apiUrl}/all${
      sort ? `?page=${page}&size=${size}&sort=${sort}${company}${alertName}${alertCluster}${alertStatus}&` : '?'
    }cacheBuster=${new Date().getTime()}`;
    return axios.get<IAlert[]>(requestUrl);
  }
);

export const getEntitiesDefault = createAsyncThunk('alert/fetch_entity_list', async ({ companyId, page, size, sort }: IPagedAlertParams) => {
  const company = `&companyId=${encodeURIComponent(companyId)}`;
  const requestUrl = `${apiUrl}/default/${sort ? `?page=${page}&size=${size}&sort=${sort}${company}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<IAlert[]>(requestUrl);
});

export const getEntities = createAsyncThunk('alert/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<IAlert[]>(requestUrl);
});

export const getEntitiesForGroup = createAsyncThunk('alert/fetch_entity_list_for_group', async (companyId?: number) => {
  const companyParam = companyId ? `?companyId=${companyId}` : '';
  return axios.get<IAlert[]>(`${apiUrl}/list${companyParam}`);
});

export const getEntity = createAsyncThunk(
  'alert/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IAlert>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'alert/create_entity',
  async (entity: IAlert, thunkAPI) => {
    const tpmsGroupType = entity.tpmsGroupType;
    const url = tpmsGroupType ? `${apiUrl}?tpmsGroupType=${tpmsGroupType}` : apiUrl;
    const result = await axios.post<IAlert>(url, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'alert/update_entity',
  async (entity: IAlert, thunkAPI) => {
    const tpmsGroupType = entity.tpmsGroupType;
    const url = tpmsGroupType ? `${apiUrl}/${entity.id}?tpmsGroupType=${tpmsGroupType}` : `${apiUrl}/${entity.id}`;
    const result = await axios.put<IAlert>(url, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'alert/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IAlert>(requestUrl);
    thunkAPI.dispatch(getAlertDataSummmary({ page: 0, size: 20, sort: 'DESC', searchName: '', searchCompanyName: '', searchCluster: '', status: 'ACTIVATED' }));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const getAlertGroups = createAsyncThunk(
  'alert/fetch_alert_groups',
  async (id: string | number) => {
    return axios.get<number[]>(`${apiUrl}/${id}/groups`);
  },
  { serializeError: serializeAxiosError }
);

export const updateAlertGroups = createAsyncThunk(
  'alert/update_alert_groups',
  async ({ id, groupIds }: { id: string | number; groupIds: number[] }, thunkAPI) => {
    const result = await axios.put<number[]>(`${apiUrl}/${id}/groups`, groupIds);
    thunkAPI.dispatch(getAlertDataSummmary({ page: 0, size: 20, sort: 'DESC', searchName: '', searchCompanyName: '', searchCluster: '', status: 'ACTIVATED' }));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const AlertSlice = createEntitySlice({
  name: 'alert',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addCase(getEntitiesForGroup.fulfilled, (state, action) => {
        state.loading = false;
        state.entitiesAlt = action.payload.data;
      })
      .addCase(getEntitiesForGroup.pending, state => {
        state.loading = true;
      })
      .addCase(getAlertGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.entitiesAlt2 = action.payload.data;
      })
      .addCase(getAlertGroups.pending, state => {
        state.loading = true;
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = AlertSlice.actions;

// Reducer
export default AlertSlice.reducer;
